import React, { useState } from 'react';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Logo } from '@/components/ui/Logo';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Sun, Moon } from 'lucide-react';

export const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { isDark, toggleTheme } = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(username, pin);
    if (!result.success) setError(result.error);
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--background)] px-4 relative">
      <div className="absolute top-5 right-5">
        <button
          onClick={toggleTheme}
          className="p-2 rounded-md border border-[var(--border)] bg-[var(--surface)] hover:bg-[var(--surface-hover)] transition-colors"
          data-testid="login-theme-toggle"
        >
          {isDark ? <Sun size={16} /> : <Moon size={16} />}
        </button>
      </div>

      {/* subtle ambient grid */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(0,229,255,0.04),transparent_50%)] pointer-events-none" />

      <div className="w-full max-w-sm relative">
        <div className="flex flex-col items-center gap-4 mb-8">
          <Logo size="xl" />
          <div className="text-center">
            <h1 className="text-2xl font-semibold tracking-tight text-[var(--text-primary)]">PC NERDS</h1>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[var(--text-muted)] mt-1">Buy • Build • Upgrade</p>
          </div>
        </div>

        <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-7 shadow-sm">
          <div className="mb-6">
            <h2 className="text-base font-medium tracking-tight text-[var(--text-primary)]">Sign in</h2>
            <p className="text-xs text-[var(--text-muted)] mt-1">Enter your credentials to continue</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="admin"
              required
              data-testid="login-username"
            />
            <Input
              label="PIN"
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="••••"
              required
              data-testid="login-pin"
            />
            {error && (
              <div className="px-3 py-2 rounded-md border border-[var(--status-danger)]/30 bg-[var(--status-danger)]/5 text-[var(--status-danger)] text-xs">
                {error}
              </div>
            )}
            <Button
              type="submit"
              variant="primary"
              className="w-full"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};
